<script setup>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import Modal from '@/components/Modal.vue';
import SchemaGroup from '@/components/Schema/SchemaGroup.vue';
import {
  ref, computed, onMounted, watch, nextTick, provide,
} from 'vue';
import { usePresetSnapshot } from '@/composables/usePresetSnapshot';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import KeyVal from '@/components/Apps/KeyValInput.vue';
import IconButton from '@/components/IconButton.vue';
import InlineLoader from '@/components/InlineLoader.vue';
import { getFrontendAppByName } from '@/assets/js/apps/apps-manager';
import { isFilenameSafe } from '@/assets/js/utils.js';

const $toast = useToast();

const store = useStore();
const currentProject = computed(() => store.state.projects.currentProject);

const emit = defineEmits(['job_started']);

const props = defineProps({
  appName: {
    type: String,
    required: true,
  },
  defaultValues: {
    type: Object,
    default: () => ({}),
  },
});

const appStartModal = ref(null);
const schemaGroupRef = ref(null);
const frontendAppRef = ref(null);
const schema = ref(null);
const is_local = ref(false);
const appDetails = ref(null);
const env = ref({});
const description = ref('');
const message = ref('');
const componentKey = ref(0);
const dataConnect = new DatastoreConnect();
const configGroupValues = ref({});
const hasSchemaData = ref(false);
const jobName = ref('');
const selectedSnapshotId = ref(null);
const selectedSnapshotName = ref('');
const infoModalOpen = ref(false);
const loading = ref(false);
const runningFrontendApp = ref(false);

const { presetSnapshotId: sharedPresetSnapshotId } = usePresetSnapshot();
provide('presetSnapshotId', selectedSnapshotId);

var appTaskId=0;

function showModal(snapshotId = null, snapshotName = '') {
  // Store snapshot context for use in pre-selecting schema fields
  selectedSnapshotId.value = snapshotId !== null ? Number(snapshotId) : null;
  selectedSnapshotName.value = snapshotName || '';
  // Write to shared composable so SchemaSnapshot can read it at any nesting depth
  sharedPresetSnapshotId.value = selectedSnapshotId.value;
  window.__appStartPresetSnapshotId = selectedSnapshotId.value;

  // Generate job name with format: appname_date using CURRENT appName
  // This ensures we always use the latest app name, not a stale one
  if (props.appName) {
    const date = new Date().toISOString().slice(0, 10); // e.g., 2026-04-01
    jobName.value = `${props.appName}_${date}`;
  }

  // Re-apply defaultValues so callers can set them just before showModal()
  configGroupValues.value = { ...props.defaultValues };
  // Force SchemaGroup to remount so it picks up the new configGroupValues
  componentKey.value += 1;

  // Always refetch on open, since appName may be static and never trigger the watcher
  getAppDetails();

  appStartModal.value.showModal();
}

function closeModal() {
  appStartModal.value.closeModal();
  sharedPresetSnapshotId.value = null;
  window.__appStartPresetSnapshotId = null;
  infoModalOpen.value = false;
  // Note: jobName is reset after API call completes
}

function showInfoModal() {
  infoModalOpen.value = !infoModalOpen.value;
}

function closeInfoModal() {
  infoModalOpen.value = false;
}

defineExpose({
  showModal,
  closeModal,
  selectedSnapshotId,
  selectedSnapshotName,
});

watch(() => props.appName, () => {
  // Reset when switching to a different app
  resetData();
  getAppDetails();
  
  // Pre-generate the job name for the new app
  if (props.appName) {
    const date = new Date().toISOString().slice(0, 10);
    jobName.value = `${props.appName}_${date}`;
  }
});

onMounted(async () => {
  getAppDetails();
});

function resetData() {
  schema.value = null;
  env.value = {};
  // Don't reset jobName here - it will be set when showModal() is called
  configGroupValues.value = { ...props.defaultValues };
}

function getSchemaData() {
  const allowedKeys = new Set();

  schema.value.forEach((field) => {
    if (field.name && field.type !== 'env' && field.type !== 'selection') {
      allowedKeys.add(field.name);
    }
    if (field.type === 'upload') {
      allowedKeys.add('folder');
    }
    if (field.type === 'snapshot') {
      allowedKeys.add('snapshot_id');
    }
  });

  return Object.fromEntries(
    Object.entries(configGroupValues.value).filter(([key]) => allowedKeys.has(key)),
  );
}

function jobStartHandler() {
  if (!isFilenameSafe(appDetails.value?.name)) {
    $toast.open({
      queue: true,
      duration: 5000,
      message: 'App name contains characters that are not valid in a file name.',
      type: 'error',
      position: 'top',
    });
    return;
  }

  if (appDetails.value?.component && frontendAppRef.value?.start) {
    runningFrontendApp.value = true;
    frontendAppRef.value.start()
      .then(() => {
        closeModal();
      })
      .catch((error) => {
        const errorMessage = error?.message || String(error || 'Failed to start app');
        $toast.open({
          queue: true,
          duration: 5000,
          message: errorMessage,
          type: 'error',
          position: 'top',
        });
      })
      .finally(() => {
        runningFrontendApp.value = false;
      });
    return;
  }

  // Validate required fields
  if (schemaGroupRef.value) {
    const missingFields = schemaGroupRef.value.validateRequiredFields();
    if (missingFields.length > 0) {
      const fieldList = missingFields.join(', ');
      $toast.open({
        queue: true,
        duration: 5000,
        message: `Required field${missingFields.length > 1 ? 's' : ''} missing: ${fieldList}`,
        type: 'error',
        position: 'top',
      });
      return;
    }
  }
  
  closeModal();

  const environ = { ...env.value };
  environ["HOST"] = window.location.hostname;
  environ["TOKEN"] = store.state.user.token;
 
  const schemaData = getSchemaData();

  dataConnect.api('job.run', { name: appDetails.value.name, env: environ, data: schemaData })
    .then((ret) => {
      if (ret.result) {
        const $toast = useToast();
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Job Started, ID:${ret.result.job_id}`,
          type: 'success',
          position: 'top',
        });

        emit('job_started', ret.result.job_id);
      } else if (ret.error) {
        const $toast = useToast();
        $toast.open({
          queue: true,
          duration: 5000,
          message: ret.error.message,
          type: 'error',
          position: 'top',
        });
      }
    })
    .catch((error) => {
      console.log(error);
      const $toast = useToast();
      $toast.open({
        queue: true,
        duration: 5000,
        message: error,
        type: 'error',
        position: 'top',
      });
    })
    .finally(() => {
      // Reset job name after API call completes (success or error)
      jobName.value = '';
    });
}

async function getAppDetails() {
  if (props.appName === '') return;

  hasSchemaData.value = false;
  loading.value = true;
  dataConnect.api("app.get", { "name": props.appName })
    .then((data) => {
      const resolvedApp = data.result || getFrontendAppByName(props.appName);

      if (resolvedApp) {
        schema.value = Array.isArray(resolvedApp.schema) ? resolvedApp.schema : [];
        appDetails.value = resolvedApp;
        description.value = resolvedApp.description || '';
        env.value = {};

        for (const entry of schema.value) {
          if (entry.type === 'env') {
            env.value = entry.values;
          } else if (entry.type !== 'selection') {
            hasSchemaData.value = true;
          }
        }
      } else if (data.error) {
        console.log(data.error);
      }
    }).catch((err) => {
      const frontendApp = getFrontendAppByName(props.appName);
      if (frontendApp) {
        schema.value = Array.isArray(frontendApp.schema) ? frontendApp.schema : [];
        appDetails.value = frontendApp;
        description.value = frontendApp.description || '';
        env.value = {};

        for (const entry of schema.value) {
          if (entry.type === 'env') {
            env.value = entry.values;
          } else if (entry.type !== 'selection') {
            hasSchemaData.value = true;
          }
        }
      } else {
        console.log(err);
      }
    }).finally(() => {
      loading.value = false;
    });
}
</script>

<template>
  <Modal
    ref="appStartModal"
    :title="appDetails?.title || appDetails?.name || 'Start App'"
    :width="'700px'"
    :height="loading ? '50vh' : undefined"
    @closed="infoModalOpen = false"
  >
    <template #modal-header>
      <IconButton
        :icon="'info-square'"
        :width="24"
        :height="24"
        :type="'button'"
        title="App Information"
        @click="showInfoModal"
      />
    </template>
    <template #modal-main>
      <div v-if="loading" class="loading-state">
        <InlineLoader width="90px" height="90px" border="12px" />
      </div>
      <div v-else class="settings scrollbar">
        <transition name="info-drawer">
          <div v-if="infoModalOpen" class="info-drawer">
            <div class="info-drawer__header">
              <h3>{{ appDetails?.title || appDetails?.name || 'App Information' }}</h3>
              <IconButton
                :icon="'close'"
                :width="18"
                :height="18"
                title="Close"
                @click="closeInfoModal"
              />
            </div>
            <p>{{ description }}</p>
            <p v-if="appDetails && appDetails.price > 0" class="cost-warning">
              <strong>Note:</strong> This app will trigger background processes which may incur costs.
            </p>
          </div>
        </transition>
        <div class="container">
          <div>
            <div v-if="Object.keys(env).length>0" class="static-card">
              <h2>Environment Variables</h2>
              <KeyVal
                v-model:data="env"
              />
            </div>
          </div>

          <div v-if="hasSchemaData" class="dynamic-card">
            <h2>Settings</h2>
            <div class="container pt-2">
              <component
                :is="appDetails.component"
                v-if="appDetails?.component"
                ref="frontendAppRef"
                :appDetails="appDetails"
              />
              <SchemaGroup
                v-else
                ref="schemaGroupRef"
                :key="componentKey"
                v-model:params="configGroupValues"
                :fields="schema"
                :defaultValues="defaultValues"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="footer">
        <div v-if="message" class="result error">
          <span>{{ message }}</span>
        </div>
        <button
          class="button modal-action-button"
          :disabled="runningFrontendApp"

          @click="jobStartHandler"
        >
          {{ appDetails?.actionLabel || 'Start App' }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';
// $cardMargin: 8px;
$cardPadding: 16px;

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 100px;
}

.settings {
  padding-top: 2px;
  padding-bottom: 16px;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  background: var(--color-white-600);

  span {
    width: 100%;
    font-size: 1rem;
    font-weight: 700;
    text-align: left;
    @include themify() {
      color: themed('color-primary');
    }
  }
}
.container {
  padding: 0 16px;
  gap: 16px;
}

h2 {
    color: #250E81;
  }

.header-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;

  h2 {
    margin: 0;
  }
}

.description-text {
  font-size: 13px;
  font-style: italic;
  margin-top: 4px;
  margin-bottom: 4px;
  line-height: 1.4;
}

.job-name-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--color-primary-200);
  border-radius: 4px;
  font-size: 14px;
  background: var(--color-white-100);
  
  &:focus {
    outline: none;
    border-color: var(--color-primary-500);
  }
  
  &::placeholder {
    color: var(--color-grey-400);
  }
}

.snapshot-context {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 4px;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 0.82rem;
  @include themify() {
    background: rgba(37, 14, 129, 0.06);
    border: 1px solid rgba(37, 14, 129, 0.15);
  }

  &__label {
    font-weight: 600;
    @include themify() {
      color: #555;
    }
  }

  &__id {
    font-weight: 700;
    font-family: monospace;
    @include themify() {
      color: #250E81;
    }
  }

  &__name {
    @include themify() {
      color: #333;
    }
  }
}

.static-card {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: $cardPadding;
  text-align: left;
  border-radius: 6px;
  height: fit-content;
  flex: 1 1 auto;
  border: 1px solid var(--color-primary-200);
  margin-bottom: 16px;
  width: 600px;
  background: var(--color-white-100);
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
}

.dynamic-card {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: $cardPadding;
  text-align: left;
  border-radius: 6px;
  height: fit-content;
  flex: 1 1 auto;
  border: none;
  width: 600px;
  background: transparent;
  box-shadow: none;
}

:deep(.dynamic-card > .container > .model-settings > .field-group:not(:first-of-type)) {
  padding-top: 24px;
}

@media (max-width: $breakpoint-lg) {
  .settings {
    width: 100%;
  }

  .container {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

}

.footer {
  display: flex;
  flex-direction: column;
  padding: 0.75rem 3rem;
  justify-content: flex-end;
  border-top: solid 3px var(--gray-300);
  background: var(--color-white-100);

  button {
    margin-left: auto;
  }
}

.result {
  display: flex;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  font-size: 1rem;
  font-weight:600;
  padding: 10px;
  align-items: center;
  justify-content: center;

  span {
    width: 100%;
    line-break: anywhere;
  }
}

.error {
  @include themify() {
    background: rgba(182, 94, 94, 0.5) ;
    color: themed('color-error') !important;
  }
}

.radio-group {
  display: inline-block;
  margin: 20px;
  border-radius: 5px;
  overflow: hidden;
  @include themify() {
    border: solid 1px themed('color-primary');
  }
}

.radio-group:focus-within, .radio-group:active {
  @include themify() {
    box-shadow:0 0 5px themed('color-primary');
  }
}

.radio-group input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.radio-group input[type="radio"] + label {
  min-width: 6rem;
  max-width: 50%;
  text-align: center;
  display: inline-block;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  line-height: 1.25;
  padding: 5px 20px;
  transition: all 0.4s ease;
  box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.25) inset;
  @include themify() {
    color: themed('color-primary');
    background: themed('color-white-100');
  }
}

.radio-group input[type="radio"]:not(:disabled):hover + label {
  @include themify() {
    background: themed('color-primary-200');
    color: themed('primary-text-color');
  }
}

.radio-group input[type="radio"]:checked + label,
.radio-group input[type="radio"]:checked:hover + label {
  opacity: 1;
  z-index: 1;
  box-shadow: 0 0 6px -2px rgba(0, 0, 0, 0.25), 0 0 6px 2px rgba(0, 0, 0, 0.25);
  @include themify() {
    background: themed('color-primary');
    color: themed('primary-text-color');
  }
}

.radio-group.full-width-selector {
  display: flex;
  width: 100%;
  margin: 0;
}

.radio-group.full-width-selector input[type="radio"] + label {
  min-width: 1px;
  padding: 3px 10px;
  flex: 1 1 auto;
}

.dataset-selector{
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: auto;

  :first-child {
    flex: 1 1 40%;
  }

  :not(:first-child) {
    flex: 1 1 60%;
  }
}

.trainer-session-row-switch {
  flex-direction: row !important;
  overflow: visible;
  align-items: center;

  label:first-child {
    flex: 1 1 40%;
    max-width: 40%;
  }
}

.full-width {
  width: 100%;
}

.custom-split-card {
  border-radius: 6px;
  padding: 10px;

  p {
    font-size: 0.9rem;
    font-weight: 700;
  }
}

.custom-split-card + .custom-split-card {
  margin-left: 15px;
}

.slider-settings {
  display: flex;
  flex-direction: row;
  padding: 12px;
  border-radius: inherit;
  gap: 6px;
  min-width: 350px;
  min-height: 50px;
  justify-content: center;
  align-items: center;
  overflow: auto;

  &__row {
    display: flex;
    align-items: center;
  }

  & input[type=checkbox] {
    margin-right: 12px;
  }

  & span {
    width: 40px;
    text-align: center;
  }
}

._train{
    border: 2px solid rgb(44, 145, 246);
}

._validation{
  @include themify {
    border: 2px solid themed('range-slider-selected-color');
  }
}

._test{
  @include themify {
    border: 2px solid themed('range-slider-selected-color-right');
  }
}

.clickable {
  cursor: pointer;
}

.split-radio[type="radio"] {
  height: 18px;
  width: 18px;
  @include themify() {
    accent-color: themed('color-primary');
  }
}

:deep() {
  .v-label{
    opacity: 1 !important;
  }
}

.checkbox-label {
  margin-left: 5px;
  font-size: 14px !important;
  font-weight: 600!important;
  letter-spacing: 0 !important;
  @include themify() {
    color: themed('form-label-color') !important;
  }
}

.info-drawer {
  overflow: hidden;
  flex-shrink: 0;
  margin: 16px 16px 0;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid var(--color-primary-200);
  background: var(--color-white-100);
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    h3 {
      margin: 0;
      color: #250E81;
      font-size: 1rem;
    }
  }

  p {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: #333;

    & + p {
      margin-top: 12px;
    }
  }
}

.info-drawer-enter-active,
.info-drawer-leave-active {
  transition: max-height 0.25s ease, opacity 0.2s ease;
  max-height: fit-content;
}

.info-drawer-enter-from,
.info-drawer-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.cost-warning {
  background-color: rgba(255, 193, 7, 0.1);
  border-left: 3px solid #FFC107;
  padding: 12px;
  border-radius: 4px;
  font-size: 13px !important;
  line-height: 1.5;
  
  strong {
    color: #F57F17;
  }
}

</style>
